import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  // Calculate pagination details
  const totalItems = pagination.totalItems || rows.length;
  const totalPages = Math.ceil(totalItems / defaultValue);
  const currentPage = pagination.page || 1;
  const canPreviousPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;

  const handlePageChange = (newPage) => {
    if (pagination.onPageChange) {
      pagination.onPageChange(newPage);
    }
  };

  const handleEntriesPerPageChange = (value) => {
    if (entriesPerPage.onChange) {
      entriesPerPage.onChange(parseInt(value, 10));
    }
  };

  // استخدم كل الصفوف بدون تقطيع محلي
  const paginatedRows = useMemo(() => rows, [rows]);

  const renderPagination = () => {
    const pageOptions = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageOptions.push(
        <MDPagination
          item
          key={i}
          onClick={() => handlePageChange(i)}
          active={currentPage === i}
        >
          {i}
        </MDPagination>
      );
    }

    return (
      <>
        {canPreviousPage && (
          <MDPagination item onClick={() => handlePageChange(currentPage - 1)}>
            <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
          </MDPagination>
        )}
        
        {startPage > 1 && (
          <>
            <MDPagination item onClick={() => handlePageChange(1)}>
              1
            </MDPagination>
            {startPage > 2 && <MDPagination item disabled>...</MDPagination>}
          </>
        )}
        
        {pageOptions}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <MDPagination item disabled>...</MDPagination>}
            <MDPagination item onClick={() => handlePageChange(totalPages)}>
              {totalPages}
            </MDPagination>
          </>
        )}
        
        {canNextPage && (
          <MDPagination item onClick={() => handlePageChange(currentPage + 1)}>
            <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
          </MDPagination>
        )}
      </>
    );
  };

  const [search, setSearch] = useState(globalFilter);
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    let sortedValue;
    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }
    return sortedValue;
  };

  const entriesStart = ((currentPage - 1) * defaultValue) + 1;
  const entriesEnd = Math.min(currentPage * defaultValue, totalItems);

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;عدد الإدخالات لكل صفحة &nbsp;&nbsp;
              </MDTypography>
              <Autocomplete
                disableClearable
                value={defaultValue.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  handleEntriesPerPageChange(newValue);
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="بحث ..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      
      <Table {...getTableProps()}>
        <MDBox component="thead" sx={{ backgroundColor: "#f5f5f5" }}>
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                  sx={{ fontSize: "0.7rem", color: "#333" }}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {paginatedRows.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow
                key={key}
                {...row.getRowProps()}
                sx={{
                  backgroundColor: key % 2 === 0 ? "#fafafa" : "#ffffff",
                  borderBottom: noEndBorder && rows.length - 1 === key ? "none" : "1px solid #ddd",
                  transition: "box-shadow 0.3s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && totalPages === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              عرض {entriesStart} إلى {entriesEnd} من {totalItems} إدخال
            </MDTypography>
          </MDBox>
        )}
        {totalPages > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {renderPagination()}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { 
    defaultValue: 10, 
    entries: [5, 10, 15, 20, 25],
    onChange: () => {} 
  },
  canSearch: false,
  showTotalEntries: true,
  pagination: { 
    variant: "gradient", 
    color: "info",
    page: 1,
    totalPages: 1,
    totalItems: 0,
    onPageChange: () => {}
  },
  isSorted: false,
  noEndBorder: false,
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.shape({
    defaultValue: PropTypes.number,
    entries: PropTypes.arrayOf(PropTypes.number),
    onChange: PropTypes.func,
  }),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    page: PropTypes.number,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    onPageChange: PropTypes.func,
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;